import { useEffect, useRef, useState, type ReactElement } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { useResizeObserver } from '@ardoq/hooks';
import { easingTokens, transitionTokens } from '@ardoq/design-tokens';
import type { PermissionContext } from '@ardoq/access-control';
import type { I18nLocale } from '@ardoq/i18n';
import { PreviewHeader } from './components/PreviewHeader';
import { PreviewDescription } from './components/PreviewDescription';
import { PreviewTags } from './components/PreviewTags';
import { PreviewDetails } from './components/PreviewDetails';
import { LoadingCard, LoadingSkeleton } from './components/PreviewSkeleton';
import {
  ErrorCard,
  NoInformationAvailable,
} from './components/PreviewEmptyStates';
import { CARD_WIDTH, CROSS_FADE_MS } from './miniOverviewConsts';
import type {
  ComponentPreview,
  ComponentPreviewState,
  MiniComponentOverviewProps,
  SkeletonVariant,
} from './types';

const settle = keyframes`
  from {
    transform: translateY(-4px);
  }
  to {
    transform: none;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const reducedMotionCSS = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const CardFrame = styled(FlexBox)`
  overflow: hidden;
  transition: height ${transitionTokens.medium} ${easingTokens.cubicOut};
  animation: ${settle} ${transitionTokens.medium} ${easingTokens.cubicOut}
    backwards;
  ${reducedMotionCSS}
`;

const MeasuredContent = styled.div`
  position: relative;
`;

const ContentLayer = styled(FlexBox)`
  animation: ${fadeIn} ${transitionTokens.medium} ${easingTokens.cubicOut}
    backwards;
  ${reducedMotionCSS}
`;

const ExitingLoaderLayer = styled(Box)`
  top: 0;
  left: 0;
  right: 0;
  opacity: 0;
  pointer-events: none;
  animation: ${fadeOut} ${transitionTokens.medium} ${easingTokens.cubicOut}
    backwards;
  ${reducedMotionCSS}
`;

type LoadedCardProps = {
  preview: ComponentPreview;
  permissionContext: PermissionContext;
  onExpand?: VoidFunction;
  i18nLocale: I18nLocale;
};

const LoadedCard = ({
  preview,
  permissionContext,
  onExpand,
  i18nLocale,
}: LoadedCardProps): ReactElement => {
  const hasDescriptionOrTags =
    Boolean(preview.component.description) || preview.tags.length > 0;
  const hasDetails = preview.detailNames.length > 0;
  const hasBody = hasDescriptionOrTags || hasDetails;

  if (!hasBody) return <NoInformationAvailable i18nLocale={i18nLocale} />;

  return (
    <Stack gap="none">
      <Box paddingBottom="medium">
        <PreviewHeader
          preview={preview}
          onExpand={onExpand}
          i18nLocale={i18nLocale}
        />
      </Box>
      {hasDescriptionOrTags && (
        <Box
          borderWidthTop={1}
          borderColor="borderSubtle"
          paddingTop="medium"
          paddingBottom={hasDetails ? 'medium' : 'none'}
        >
          <Stack gap="small">
            {preview.component.description && (
              <PreviewDescription
                description={preview.component.description}
                i18nLocale={i18nLocale}
              />
            )}
            {preview.tags.length > 0 && <PreviewTags tags={preview.tags} />}
          </Stack>
        </Box>
      )}
      {hasDetails && (
        <PreviewDetails
          detailNames={preview.detailNames}
          component={preview.component}
          enhancedScopeData={preview.enhancedScopeData}
          permissionContext={permissionContext}
        />
      )}
    </Stack>
  );
};

type CardContentProps = {
  state: ComponentPreviewState;
  permissionContext: PermissionContext;
  onExpand?: VoidFunction;
  i18nLocale: I18nLocale;
  skeletonVariant: SkeletonVariant;
};

const CardContent = ({
  state,
  permissionContext,
  onExpand,
  i18nLocale,
  skeletonVariant,
}: CardContentProps): ReactElement => {
  if (state.status === 'loading')
    return <LoadingCard i18nLocale={i18nLocale} variant={skeletonVariant} />;
  if (state.status === 'error') return <ErrorCard i18nLocale={i18nLocale} />;
  return (
    <LoadedCard
      preview={state.preview}
      permissionContext={permissionContext}
      onExpand={onExpand}
      i18nLocale={i18nLocale}
    />
  );
};

const useExitingLoader = (isLoading: boolean): boolean => {
  const [isExiting, setIsExiting] = useState(false);
  const wasLoadingRef = useRef(isLoading);

  useEffect(() => {
    const wasLoading = wasLoadingRef.current;
    wasLoadingRef.current = isLoading;
    if (!wasLoading || isLoading) return;

    setIsExiting(true);
    const timeoutId = window.setTimeout(
      () => setIsExiting(false),
      CROSS_FADE_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [isLoading]);

  return isExiting;
};

export const MiniComponentOverview = ({
  state,
  permissionContext,
  onExpand,
  i18nLocale,
  skeletonVariant = 'default',
}: MiniComponentOverviewProps): ReactElement => {
  const isLoaderExiting = useExitingLoader(state.status === 'loading');
  const contentRef = useRef<HTMLDivElement>(null);
  const { height } = useResizeObserver(contentRef);

  return (
    <CardFrame
      flexDirection="column"
      minWidth={CARD_WIDTH}
      maxWidth={CARD_WIDTH}
      style={height === undefined ? undefined : { height }}
    >
      <MeasuredContent ref={contentRef}>
        <ContentLayer key={state.status} flexDirection="column">
          <CardContent
            state={state}
            permissionContext={permissionContext}
            onExpand={onExpand}
            i18nLocale={i18nLocale}
            skeletonVariant={skeletonVariant}
          />
        </ContentLayer>
        {isLoaderExiting && (
          <ExitingLoaderLayer position="absolute" aria-hidden="true">
            <LoadingSkeleton variant={skeletonVariant} />
          </ExitingLoaderLayer>
        )}
      </MeasuredContent>
    </CardFrame>
  );
};
